import { useAuthStore } from '@/stores/auth-store';
import { useFeatureFlagsReady } from '@/services/feature-flags';
import type { FeatureFlagDefinition } from '@/config/feature-flags';
import { useFeatureFlag } from './use-feature-flag';

export interface GatedFlagAccess {
  /** Whether the flagged surface should be shown. */
  allowed: boolean;
  /** Whether the decision is settled, so a caller can hold rather than redirect. */
  ready: boolean;
}

/** Test-only override (set by e2e via addInitScript). Ignored in production builds. */
const testOverride = (key: string): boolean | undefined => {
  if (import.meta.env.PROD) return undefined;
  return (globalThis as unknown as Record<string, boolean | undefined>)[key];
};

/**
 * A route-level flag gate: the flag's value, plus whether it is safe to act on it yet.
 *
 * `ready` waits for the user as well as the provider: a flag can be targeted per tenant, and
 * its value for `anonymous` on a cold load is a different question from its value for this
 * user. Deciding on the anonymous pass would bounce a user whose tenant has it on.
 *
 * `overrideKey` names the window value e2e sets through `setFlagOverride`.
 */
export function useGatedFlagAccess(
  flag: FeatureFlagDefinition,
  overrideKey: string,
): GatedFlagAccess {
  const user = useAuthStore((s) => s.user);
  const flagOn = useFeatureFlag(flag);
  const flagsReady = useFeatureFlagsReady();

  const override = testOverride(overrideKey);
  if (override !== undefined) return { allowed: override, ready: true };
  return { allowed: flagOn, ready: user !== null && flagsReady };
}
